import captionService, { type ResourceInfo } from "CaptionService";
import { createDisposable, type Disposable } from "Disposable";
import { getInterfaceName, getPrimaryKey, isDeletedOrDetached, isEntity } from "EntityExtensions";
import { subscribeToEntityFetched, unsubscribeFromEntityFetched } from "EntityQuerySubscribers";
import entitySaveService from "EntitySaveService";
import { getEntityTypeNameFromExtendedTypeName, isCollectionType } from "EntityTypeUtils";
import favoritesService from "FavoritesService";
import { type FormFlowDefinition, type FormFlowVariable } from "FormFlowTypes";
import lookupDisplayModeHelper from "LookupDisplayModeHelper";
import RuleService from "RuleService";
import { joinUri } from "UriUtils";
import { type Entity } from "breeze-client";

export function usingAddToRecentTracker(definition: FormFlowDefinition, entityKey: string): Disposable | undefined {
  if (!definition || definition.DisableAddToRecents) {
    return;
  }

  let lastDisplayName: string | undefined;

  if (isFormFlowForNewUsage(definition)) {
    addToRecentsForFormFlow(definition);

    const entityInterfaceName = getEntityTypeForForNewUsage(definition);
    if (entityInterfaceName) {
      const addSavedEntitiesAsync = async (savedEntities: Entity[]): Promise<void> => {
        for (const savedEntity of savedEntities) {
          if (!isDeletedOrDetached(savedEntity) && entityInterfaceName === getInterfaceName(savedEntity)) {
            await addToRecentsIfNeededAsync(savedEntity);
          }
        }
      };
      entitySaveService.subscribe(addSavedEntitiesAsync);
      return createDisposable(() => entitySaveService.unsubscribe(addSavedEntitiesAsync));
    }
  } else if (entityKey) {
    const entityInterfaceName = getEntityTypeForMaintainUsage(definition);
    if (entityInterfaceName) {
      subscribeToEntityFetched(entityInterfaceName, entityKey, addFetchedEntityAsync);
      entitySaveService.subscribe(addModifiedEntitiesAsync);

      return createDisposable(() => {
        unsubscribeFromEntityFetched(entityInterfaceName, entityKey, addFetchedEntityAsync);
        entitySaveService.unsubscribe(addModifiedEntitiesAsync);
      });
    }
  }

  return undefined;

  async function addToRecentsIfNeededAsync(entity: Entity): Promise<void> {
    if (entity.entityAspect.entityState?.isUnchangedOrModified()) {
      const displayName = await getEntityDisplayNameAsync(entity);
      if (displayName && displayName !== lastDisplayName) {
        lastDisplayName = displayName;
        addToRecentsForEntity(entity, displayName);
      }
    }
  }

  async function addFetchedEntityAsync(fetchedEntity: Entity): Promise<void> {
    await addToRecentsIfNeededAsync(fetchedEntity);
  }

  async function addModifiedEntitiesAsync(savedEntities: Entity[]): Promise<void> {
    for (const savedEntity of savedEntities) {
      if (!isDeletedOrDetached(savedEntity) && entityKey === getPrimaryKey(savedEntity)) {
        await addToRecentsIfNeededAsync(savedEntity);
      }
    }
  }
}

export async function addToRecentAsync(
  definition: FormFlowDefinition,
  entity?: Entity,
  queryParameters?: { key: string; value: unknown }[],
  captionOverride?: ResourceInfo,
): Promise<void> {
  let primaryKey: string;
  let uri = `#/formFlow/${encodeURIComponent(definition.PK)}`;
  const subtitlePromises = [];

  if (entity) {
    primaryKey = getPrimaryKey(entity);
    if (typeof primaryKey === "string") {
      uri = joinUri(uri, encodeURIComponent(primaryKey));
      subtitlePromises.push(getEntityDisplayNameAsync(entity));
    }
  }

  if (queryParameters) {
    const queryStringParts: string[] = [];
    queryParameters.forEach(({ key, value }) => {
      if (value) {
        if (isEntity(value)) {
          primaryKey = getPrimaryKey(value);
          queryStringParts.push(`${key}=${encodeURIComponent(primaryKey)}`);
          subtitlePromises.push(getEntityDisplayNameAsync(value as Entity));
        } else if (typeof value === "string" || typeof value === "boolean" || typeof value === "number") {
          queryStringParts.push(`${key}=${encodeURIComponent(value)}`);
          subtitlePromises.push(Promise.resolve(value));
        }
      }
    });

    if (queryStringParts.length) {
      uri = `${uri}?${queryStringParts.join("&")}`;
    }
  }

  const subtitles: string[] = await Promise.all(subtitlePromises);
  const formFlowTitle = captionService.getStringFromInfo(captionOverride || definition.Caption);
  const title = subtitles.length ? `${formFlowTitle} (${subtitles.join(", ")})` : formFlowTitle;

  return favoritesService.addToRecentsAsync({
    Uri: uri,
    Title: title,
    LinkType: favoritesService.LinkTypes.RecentFormFlows,
  });
}

function isFormFlowForNewUsage(definition: FormFlowDefinition): boolean {
  return !definition.Parameters || definition.Parameters.length === 0;
}

function addToRecentsForFormFlow(definition: FormFlowDefinition): void {
  favoritesService.addToRecentsAsync({
    Uri: `#/formFlow/${definition.PK}`,
    Title: captionService.getStringFromInfo(definition.Caption),
    EntityType: definition.EntityTypeName,
    LinkType: favoritesService.LinkTypes.RecentFormFlows,
  });
}

function getEntityTypeForForNewUsage(definition: FormFlowDefinition): string | undefined {
  const variables =
    definition.EntityTypeName &&
    "LocalVariables" in definition &&
    definition.LocalVariables &&
    definition.LocalVariables.filter((v: FormFlowVariable) => isVariableOfType(v, definition.EntityTypeName));

  return variables && variables.length === 1 ? definition.EntityTypeName : undefined;
}

function getEntityTypeForMaintainUsage(definition: FormFlowDefinition): string | undefined {
  const hasValidParameter =
    definition.EntityTypeName &&
    definition.Parameters?.length === 1 &&
    isVariableOfType(definition.Parameters[0], definition.EntityTypeName);

  return hasValidParameter ? definition.EntityTypeName : undefined;
}

function isVariableOfType(variable: FormFlowVariable, entityTypeName?: string): boolean {
  return (
    getEntityTypeNameFromExtendedTypeName(variable.VariableTypeName) === entityTypeName &&
    !isCollectionType(variable.VariableTypeName)
  );
}

export async function toggleEntityFavoriteAsync(entity: Entity): Promise<void> {
  const recent = {
    Title: await getEntityDisplayNameAsync(entity),
    EntityType: getInterfaceName(entity),
    EntityPK: getPrimaryKey(entity),
    LinkType: favoritesService.LinkTypes.RecentEntities,
  };

  if (favoritesService.isFavorite(recent)) {
    await favoritesService.removeFromFavoritesAsync(recent);
  } else {
    await favoritesService.addToFavoritesAsync(recent);
  }
}

export function isEntityFavorite(entity: Entity): boolean {
  return favoritesService.isFavorite({
    EntityType: getInterfaceName(entity),
    EntityPK: getPrimaryKey(entity),
  });
}

function addToRecentsForEntity(entity: Entity, displayName: string): void {
  favoritesService.addToRecentsAsync({
    Title: displayName,
    EntityType: getInterfaceName(entity),
    EntityPK: getPrimaryKey(entity),
    LinkType: favoritesService.LinkTypes.RecentEntities,
  });
}

function getEntityDisplayNameAsync(entity: Entity): Promise<string> {
  const rules = RuleService.get(getInterfaceName(entity));
  const displayMode = lookupDisplayModeHelper.getDisplayMode(rules);
  return lookupDisplayModeHelper.getDisplayValueAsync(
    entity,
    displayMode,
    rules.codeProperty(),
    rules.descriptionProperty(),
  );
}
